import { useRouter } from '@tanstack/react-router'
import { useCallback } from 'react'

/**
 * A host-relative path, or null. `//evil.example` is another origin and a
 * `javascript:` URL runs script, so only a single leading slash passes.
 */
export function hostPath(url: unknown): string | null {
  return typeof url === 'string' && /^\/(?!\/)/.test(url) ? url : null
}

/**
 * Opens one of this app's own pages from a card (a Links chip, a
 * Destination button): through the app's router, in this tab, so the
 * conversation's state is kept. Anything but a host-relative path is
 * refused. Outside a router (a card drawn on its own) the page is loaded
 * plainly, still on this origin only.
 *
 * Never `window.parent.postMessage(..., '*')`: nothing in this app listens
 * for it, so the control did nothing, and a page that framed the app would
 * have read every path the reader tapped.
 */
export function useOpenPath(): (path: string) => void {
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | null
  return useCallback(
    (path: string) => {
      const safe = hostPath(path)
      if (safe === null) return
      if (router === null) {
        window.location.assign(safe)
        return
      }
      void router.navigate({ href: safe })
    },
    [router],
  )
}

type AnyRouter = ReturnType<typeof useRouter>

/**
 * Whether `path` lands on a page of this app: a host path that the router's
 * own route matching resolves to a route, never a list of known paths. A path
 * it matches only in part (`/stocks/NABIL/nowhere`, whose rest the router
 * calls `**`) or not at all would open PageNotFound. The query and hash are
 * not part of the match.
 */
export function routesTo(router: AnyRouter, path: string): boolean {
  const safe = hostPath(path)
  if (safe === null) return false
  const pathname = safe.split(/[?#]/, 1)[0] ?? safe
  const [, params, found] = router.getMatchedRoutes(pathname)
  return found !== undefined && !('**' in params)
}

/**
 * Whether a card may draw a control that opens `path` (a Links chip, a
 * Destination button): only a page the app's router has. Outside a router
 * (a card drawn on its own) any host path, as before.
 */
export function useRoutable(): (path: string) => boolean {
  const router = useRouter({ warn: false }) as AnyRouter | null
  return useCallback(
    (path: string) => (router === null ? hostPath(path) !== null : routesTo(router, path)),
    [router],
  )
}
